import React, {FC, useCallback, useEffect, useMemo} from 'react';

import {useIsSingleRun} from '../../pages/Run/RunWorkspaceRunContext';
import {usePanelBankSectionsPagingContext} from '../../state/panelbank/PanelBankSectionsPagingContext';
import {usePanelSearchDebouncedContext} from '../../state/panelbank/PanelSearchContextProvider';
import * as ViewHooks from '../../state/views/hooks';
import {usePanelBankConfig} from '../../state/views/panelBankConfig/hooks';
import {panelIsActive} from '../../util/panelbank';
import {getFlowSectionsWithVisiblePanels} from '../../util/panelbankFlow';
import {LayedOutPanel} from '../../util/panelTypes';
import * as RunHelpers from '../../util/runhelpers';
import {CreateReportModal} from '../CreateReportModal';
import {useWorkspaceSettingsForReportCreation} from '../CreateReportModal/useReportSettings';
import {useKeyInfoQueryContext} from '../MultiRunWorkspace/KeyInfoQueryContext';
import {usePanelBankSectionsScrollContext} from '../PanelBank/PanelBankSectionsScrollContextProvider';
import {usePanelBankWidthContext} from '../PanelBank/PanelBankWidthContext';
import {PanelBankSectionConfig} from '../PanelBank/types';
import {useWorkspaceRefsContext} from '../Workspace/WorkspaceRefsContext';
import {EditablePanel} from './EditablePanel';
import {usePanelContext} from './PanelContextProvider';
import {PanelProps} from './types';

export const EditablePanelFullScreenWrapper: FC<PanelProps> = props => {
  const {panelBankConfigRef} = useWorkspaceRefsContext();
  const {workspaceSettingsRef: reportWorkspaceSettingsRef} =
    useWorkspaceRefsContext();
  const {panelBankSectionConfigRef} = usePanelContext();
  const panelBankSectionConfig = ViewHooks.usePartMaybe(
    panelBankSectionConfigRef
  );
  const {setScrollToPanelBankSectionId} = usePanelBankSectionsScrollContext();

  useEffect(() => {
    setScrollToPanelBankSectionId(panelBankSectionConfig?.__id__);
  }, [panelBankSectionConfig?.__id__, setScrollToPanelBankSectionId]);

  const reportInheritedSettings = useWorkspaceSettingsForReportCreation(
    reportWorkspaceSettingsRef
  );
  const isSingleRun = useIsSingleRun();

  const {debouncedSearchRegex: searchRegex} = usePanelSearchDebouncedContext();

  const {
    keyInfoQuery: {historyKeyInfo},
  } = useKeyInfoQueryContext();
  const keyTypes = RunHelpers.useKeyTypes(historyKeyInfo.keys);

  const {panelBankWidth} = usePanelBankWidthContext();
  const usePanelIsActive = useCallback(
    (section: PanelBankSectionConfig, panel: LayedOutPanel) =>
      panelIsActive({
        section,
        panel,
        isSingleRun,
        searchRegex,
        historyKeyInfo,
        keyTypes,
      }),
    [isSingleRun, searchRegex, historyKeyInfo, keyTypes]
  );
  const {currentPageBySectionRefID} = usePanelBankSectionsPagingContext();

  const useExportableSections = () => {
    const {sections} = usePanelBankConfig(panelBankConfigRef);
    return useMemo(() => {
      return getFlowSectionsWithVisiblePanels({
        sections,
        currentPageBySectionRefID,
        isSingleRun,
        searchRegex,
        historyKeyInfo,
        keyTypes,
        panelBankWidth,
      });
    }, [sections]);
  };

  return (
    <>
      <EditablePanel {...props} />
      <CreateReportModal
        inheritedSettings={reportInheritedSettings}
        useExportableSections={useExportableSections}
        panelIsActive={usePanelIsActive}
      />
    </>
  );
};
